import { gql, useQuery, useLazyQuery, DocumentNode } from '@apollo/client';
import { BorrowerData } from '../services/broker.service';
import { useCallback, useEffect, useRef, useState } from 'react';

const GET_BORROWERS: DocumentNode = gql`
  query GetBorrowersPaginated($search: String, $page: Int, $limit: Int) {
    getBorrowers(search: $search, page: $page, limit: $limit) {
      borrowers {
        _id
        name
        lastName
        email
        phone
      }
      total
      hasMore
    }
  }
`;

const CHECK_BORROWER_EMAIL_EXISTS: DocumentNode = gql`
  query CheckBorrowerEmailExists($email: String!) {
    checkBorrowerEmailExists(email: $email)
  }
`;

export const useGetBorrowers = (search?: string) => {
  const [page, setPage] = useState(1);
  const [allBorrowers, setAllBorrowers] = useState<BorrowerData[]>([]);
  const previousSearchRef = useRef<string | undefined>(search);

  // Resetear paginación cuando cambia el search
  useEffect(() => {
    if (previousSearchRef.current !== search) {
      setPage(1);
      previousSearchRef.current = search;
    }
  }, [search]);

  const result = useQuery<{
    getBorrowers: {
      borrowers: BorrowerData[];
      total: number;
      hasMore: boolean;
    };
  }>(GET_BORROWERS, {
    variables: {
      search: search || null,
      page,
      limit: 10,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (page === 1) {
        setAllBorrowers(data.getBorrowers.borrowers);
      } else {
        // Páginas siguientes: agregar
        setAllBorrowers((prev) => {
          const newBorrowers = data.getBorrowers.borrowers.filter(
            (newB) => !prev.some((existingB) => existingB._id === newB._id)
          );
          return [...prev, ...newBorrowers];
        });
      }
    },
  });

  const loadMore = useCallback(() => {
    if (result.data?.getBorrowers.hasMore && !result.loading) {
      setPage((prev) => prev + 1);
    }
  }, [result.data?.getBorrowers.hasMore, result.loading, page]);

  const resetPagination = useCallback(() => {
    setPage(1);
  }, []);

  return {
    ...result,
    borrowers: allBorrowers,
    hasMore: result.data?.getBorrowers.hasMore || false,
    total: result.data?.getBorrowers.total || 0,
    loadMore,
    resetPagination,
    isLoadingMore: result.loading && page > 1,
  };
};

export const useCheckBorrowerEmailExists = () => {
  const [checkEmail, { data, loading, error }] = useLazyQuery<{ checkBorrowerEmailExists: boolean }>(
    CHECK_BORROWER_EMAIL_EXISTS
  );

  return {
    checkEmail,
    emailExists: data?.checkBorrowerEmailExists || false,
    loading,
    error
  };
};
